/**
 * NIHARA STUDIO - LUXURY EDITORIAL HERO BANNER / SLIDESHOW ENGINE
 * High-Performance ES6 Vanilla JS Class | Zero Dependencies | Shopify Theme Editor Ready
 */

class HeroEditorial {
  constructor(container) {
    if (!container) return;
    this.container = container;

    // Core Elements
    this.viewport = this.container.querySelector('.nihara-hero__viewport');
    this.track = this.container.querySelector('.nihara-hero__track');
    this.slides = Array.from(this.container.querySelectorAll('.nihara-hero__slide'));
    this.prevBtn = this.container.querySelector('.nihara-hero__arrow--prev');
    this.nextBtn = this.container.querySelector('.nihara-hero__arrow--next');
    this.indicators = Array.from(this.container.querySelectorAll('[data-slide-index]'));
    this.progressFill = this.container.querySelector('.nihara-hero__progress-fill');
    this.fractionCurrent = this.container.querySelector('.nihara-hero__fraction-current');
    this.magneticBtns = Array.from(this.container.querySelectorAll('.nihara-hero__btn--magnetic'));

    // Options from Dataset
    this.autoplay = this.container.dataset.autoplay === 'true';
    this.speed = parseInt(this.container.dataset.speed, 10) || 6000; // ms per slide
    this.transition = this.container.dataset.transition || 'fade';
    this.pauseOnHover = this.container.dataset.pauseHover === 'true';
    this.loop = this.container.dataset.loop !== 'false';

    // State Variables
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.progressStartTime = null;
    this.progressReqAnim = null;
    this.observer = null;

    // Touch & Swipe Physics
    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;
    this.swipeThreshold = 50;

    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;

    // Set initial active state
    this.setActiveSlide(this.currentIndex, false);

    // Event Listeners
    this.bindEvents();

    // Touch Gestures
    this.setupTouchGestures();

    // Magnetic Buttons Hover Effect
    if (this.magneticBtns.length > 0) {
      this.setupMagneticButtons();
    }

    // Parallax Mouse Effect
    this.setupParallax();

    // Intersection Observer for Performance (pause videos/autoplay when out of viewport)
    this.setupIntersectionObserver();

    // Shopify Theme Editor Event Listeners
    this.setupShopifyThemeEditorEvents();

    // Start Autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    this.indicators.forEach((indicator) => {
      indicator.addEventListener('click', (e) => {
        const targetIndex = parseInt(e.currentTarget.dataset.slideIndex, 10);
        if (!isNaN(targetIndex) && targetIndex !== this.currentIndex) {
          this.goToSlide(targetIndex);
        }
      });
    });

    // Pause on Hover
    if (this.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
    }

    // Keyboard Navigation
    document.addEventListener('keydown', (e) => {
      if (this.isElementInViewport(this.container)) {
        if (e.key === 'ArrowLeft') this.prevSlide();
        if (e.key === 'ArrowRight') this.nextSlide();
      }
    });
  }

  setActiveSlide(index, animate = true) {
    if (index < 0 || index >= this.totalSlides) return;

    this.isAnimating = true;

    // Deactivate previous slide
    this.slides.forEach((slide, i) => {
      const isTarget = i === index;
      slide.classList.toggle('is-active', isTarget);
      slide.setAttribute('aria-hidden', !isTarget);

      // Handle Videos
      const video = slide.querySelector('video');
      if (video) {
        if (isTarget) {
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              /* Autoplay blocked fallback */
            });
          }
        } else {
          video.pause();
        }
      }
    });

    // Update Track Transform if horizontal slide transition
    if (this.transition === 'slide' || this.transition === 'directional-slide') {
      this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
    }

    // Update Fraction Counter
    if (this.fractionCurrent) {
      this.fractionCurrent.textContent = String(index + 1).padStart(2, '0');
    }

    // Update Indicators
    this.indicators.forEach((ind) => {
      const indIndex = parseInt(ind.dataset.slideIndex, 10);
      ind.classList.toggle('is-active', indIndex === index);
    });

    // Reset Progress Bar Animation
    if (this.autoplay) {
      this.animateProgressBar();
    }

    this.currentIndex = index;

    setTimeout(() => {
      this.isAnimating = false;
    }, 800);
  }

  goToSlide(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    this.resetAutoplay();
    this.setActiveSlide(index);
  }

  nextSlide() {
    if (this.isAnimating) return;
    let nextIndex = this.currentIndex + 1;
    if (nextIndex >= this.totalSlides) {
      if (this.loop) nextIndex = 0;
      else return;
    }
    this.resetAutoplay();
    this.setActiveSlide(nextIndex);
  }

  prevSlide() {
    if (this.isAnimating) return;
    let prevIndex = this.currentIndex - 1;
    if (prevIndex < 0) {
      if (this.loop) prevIndex = this.totalSlides - 1;
      else return;
    }
    this.resetAutoplay();
    this.setActiveSlide(prevIndex);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.animateProgressBar();
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.speed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    if (this.progressReqAnim) {
      cancelAnimationFrame(this.progressReqAnim);
      this.progressReqAnim = null;
    }
    if (this.progressFill) {
      this.progressFill.style.width = '0%';
    }
  }

  resetAutoplay() {
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  animateProgressBar() {
    if (!this.progressFill) return;
    if (this.progressReqAnim) cancelAnimationFrame(this.progressReqAnim);

    this.progressStartTime = performance.now();

    const updateProgress = (currentTime) => {
      const elapsed = currentTime - this.progressStartTime;
      const progressPercent = Math.min((elapsed / this.speed) * 100, 100);
      this.progressFill.style.width = `${progressPercent}%`;

      if (elapsed < this.speed && this.autoplayTimer) {
        this.progressReqAnim = requestAnimationFrame(updateProgress);
      }
    };

    this.progressReqAnim = requestAnimationFrame(updateProgress);
  }

  setupTouchGestures() {
    if (!this.viewport) return;

    const handleTouchStart = (e) => {
      this.startX = e.touches ? e.touches[0].clientX : e.clientX;
      this.isDragging = true;
    };

    const handleTouchMove = (e) => {
      if (!this.isDragging) return;
      this.currentX = e.touches ? e.touches[0].clientX : e.clientX;
    };

    const handleTouchEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const diffX = this.startX - this.currentX;

      if (Math.abs(diffX) > this.swipeThreshold) {
        if (diffX > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
      this.startX = 0;
      this.currentX = 0;
    };

    this.viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', handleTouchMove, { passive: true });
    this.viewport.addEventListener('touchend', handleTouchEnd, { passive: true });

    this.viewport.addEventListener('mousedown', handleTouchStart);
    this.viewport.addEventListener('mousemove', handleTouchMove);
    this.viewport.addEventListener('mouseup', handleTouchEnd);
  }

  setupMagneticButtons() {
    this.magneticBtns.forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        btn.style.transform = `translate3d(${x * 0.25}px, ${y * 0.25}px, 0)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'translate3d(0, 0, 0)';
      });
    });
  }

  setupParallax() {
    const parallaxMedia = this.container.querySelectorAll('.nihara-hero__media-parallax');
    if (parallaxMedia.length === 0) return;

    this.container.addEventListener('mousemove', (e) => {
      const rect = this.container.getBoundingClientRect();
      const relativeX = (e.clientX - rect.left) / rect.width - 0.5;
      const relativeY = (e.clientY - rect.top) / rect.height - 0.5;

      parallaxMedia.forEach((media) => {
        media.style.transform = `translate3d(${relativeX * -25}px, ${relativeY * -25}px, 0) scale(1.08)`;
      });
    });
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.autoplay && !this.autoplayTimer) this.startAutoplay();
            const activeVideo = this.slides[this.currentIndex]?.querySelector('video');
            if (activeVideo) activeVideo.play();
          } else {
            this.stopAutoplay();
            const activeVideo = this.slides[this.currentIndex]?.querySelector('video');
            if (activeVideo) activeVideo.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    this.observer.observe(this.container);
  }

  setupShopifyThemeEditorEvents() {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this.container)) {
          this.init();
        }
      });

      document.addEventListener('shopify:slide:select', (e) => {
        const slideBlock = e.target;
        const slideIndex = this.slides.indexOf(slideBlock);
        if (slideIndex !== -1) {
          this.stopAutoplay();
          this.setActiveSlide(slideIndex, false);
        }
      });

      document.addEventListener('shopify:slide:deselect', () => {
        if (this.autoplay) this.startAutoplay();
      });
    }
  }

  isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  destroy() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
  }
}

// Auto-initialize all instance sections on DOM loaded
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="hero-editorial"]');
  sections.forEach((section) => {
    new HeroEditorial(section);
  });
});
